import EditNameModal from '@/components/features/data/EditNameModal'
import { CreateProjectModal } from '@/components/features/projects/CreateProjectModal'
import { FolderBreadcrumb } from '@/components/features/projects/FolderBreadcrumb'
import { FolderCard } from '@/components/features/projects/FolderCard'
import { FolderTreeNav } from '@/components/features/projects/FolderTreeNav'
import { GroupProjectsModal } from '@/components/features/projects/GroupProjectsModal'
import { ModalFormError } from '@/components/features/projects/ModalFormError'
import { MoveToFolderModal } from '@/components/features/projects/MoveToFolderModal'
import {
  ProjectCard,
  type ProjectRollup,
} from '@/components/features/projects/ProjectCard'
import {
  ProjectsTable,
  type ProjectsTableSort,
} from '@/components/features/projects/ProjectsTable'
import { RenameFolderModal } from '@/components/features/projects/RenameFolderModal'
import { useOrgFolders } from '@/components/features/projects/useOrgFolders'
import { useProjectBoardDnd } from '@/components/features/projects/useProjectBoardDnd'
import FlyoutMenu from '@/components/layout/FlyoutMenu'
import MenuItem from '@/components/layout/FlyoutMenu/MenuItem'
import Modal from '@/components/layout/Modal'
import ConfirmationDialog from '@/components/utils/ConfirmationDialog'
import { useClient } from '@/context/ClientContext'
import { usePermissions } from '@/context/PermissionsContext'
import { useOffsetPaginationQuery } from '@/hooks/useOffsetPaginationQuery'
import { useOrganisationUsersWithUserMap } from '@/hooks/useOrganisationUsersWithUserMap'
import { usePermissionCheck } from '@/hooks/usePermissionCheck'
import { useUserPreferences } from '@/hooks/useUserPreferences'
import {
  fetchGroups,
  fetchOrganisationUsers,
  fetchProjectsPaginated,
} from '@/lib/clientHelpers'
import { userFor } from '@/lib/userDisplay'
import { cn, getUserFullName } from '@/lib/utils'
import type { ViewMode } from '@/types/UserPreferences'
import { DndContext, DragOverlay, pointerWithin } from '@dnd-kit/core'
import { snapCenterToCursor } from '@dnd-kit/modifiers'
import {
  ArrowsUpDownIcon,
  Bars3Icon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  FolderIcon,
  FolderPlusIcon,
  MagnifyingGlassIcon,
  PhotoIcon,
  PlusIcon,
  Squares2X2Icon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline'
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'
import { z } from 'zod'

import type { AssetServiceClient } from '@droidmap/asset-service-contract'
import { ToggleGroup, ToggleGroupItem } from '@droidmap/frontend-ui'
import { Pagination } from '@droidmap/frontend-ui'
import { Button } from '@droidmap/frontend-ui'
import { Input } from '@droidmap/frontend-ui'
import { Spinner } from '@droidmap/frontend-ui'
import {
  type InitialProjectGrant,
  Permission,
} from '@droidmap/permissions-service-contract'
import {
  FOLDER_MAX_DEPTH,
  type FolderRef,
  type FolderSummary,
  type ListProjectsByOrganisationResult,
  type Project,
  type ProjectListSortKey,
  isValidFolderName,
} from '@droidmap/project-service-contract'
import {
  type DroidMapMetadata,
  SortDirection,
} from '@droidmap/shared-service-contract'
import { serverRefusalMessage } from '@droidmap/shared-utils'

const PREFERENCE_KEY = 'ORGANISATION_PROJECTS'
// Project list cache is keyed by org + folder so navigating folders doesn't
// serve another folder's page. `''` stands in for "no folder" (root): a real
// folder id is a non-empty ULID, so it can't collide with the sentinel.
const projectsQueryKey = (orgId: string, folderId: string | undefined) =>
  ['projects', orgId, folderId ?? ''] as const

// Grid-view track geometry: 142px min tracks, and every item (folder card and
// project card alike) spans two tracks, so each lands on its designed ~300px
// (2×142 + the 16px gap). Both kinds share one grid, so they line up
// column-for-column by construction. Below `sm` the grid pins to two tracks so
// a span-2 item can't overflow into an implicit column on narrow screens.
const SECTION_GRID =
  'grid grid-cols-2 gap-4 sm:grid-cols-[repeat(auto-fill,minmax(142px,1fr))]'

const projectsSearchSchema = z.object({
  // Current folder (a folder-entity ULID). Absent = org root. No normalize
  // transform — it's an opaque id, not a path.
  folderId: z.string().optional(),
})

export const Route = createFileRoute('/_authenticated/orgs/$orgId/projects')({
  validateSearch: projectsSearchSchema,
  component: Projects,
  // The header (route) breadcrumb is static: the loader only has the folder
  // id, and deriving its name would need a fetch. The left folder tree carries
  // the full folder location instead.
  loader: () => ({ crumb: 'Projects' }),
})

function useProjectRollups(
  projectIds: string[],
  assetClient: AssetServiceClient,
  metadata: DroidMapMetadata,
): Map<string, ProjectRollup | undefined> {
  const queries = useQueries({
    queries: projectIds.map((projectId) => ({
      queryKey: ['project-rollup', projectId],
      queryFn: async (): Promise<ProjectRollup> => {
        // One call yields the set count, the geographic bounding box and a
        // hero thumbnail — the asset-service owns all three and aggregates
        // them server-side, so the card no longer pages assets itself.
        const summary = await assetClient.getProjectAssetSummary(
          { projectId },
          { ...metadata, projectId },
        )
        return {
          assetSetCount: summary.assetSetCount,
          boundingBox: summary.boundingBox,
          thumbnailUrl: summary.thumbnailPresignedUrl,
        }
      },
      staleTime: 60_000,
    })),
  })

  return useMemo(() => {
    const map = new Map<string, ProjectRollup | undefined>()
    projectIds.forEach((id, i) => {
      map.set(id, queries[i]?.data)
    })
    return map
  }, [projectIds, queries])
}

interface CreateProjectCardProps {
  onClick: () => void
}

function CreateProjectCard({ onClick }: CreateProjectCardProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'group border-border-strong relative flex flex-col items-center justify-center gap-4 rounded-lg border border-dashed bg-transparent p-9 text-center transition-all duration-b ease-meridian',
        'hover:border-accent-tint hover:bg-accent-soft hover:-translate-y-0.5',
        'focus-visible:border-accent-tint focus-visible:ring-accent-soft focus-visible:outline-hidden focus-visible:ring-2',
        'min-h-full',
      )}
    >
      <div
        className={cn(
          'border-border-strong text-text-dim grid h-13 w-13 place-items-center rounded-full border border-dashed transition-all duration-150',
          'group-hover:border-accent group-hover:text-accent group-hover:bg-panel group-hover:border-solid group-hover:shadow-[0_0_0_4px_var(--accent-soft)]',
        )}
      >
        <PlusIcon className="h-6 w-6" />
      </div>
      <div>
        <h3 className="text-text mb-1.5 text-[15px] font-semibold">
          New project
        </h3>
        <p className="text-text-dim mx-auto max-w-[220px] text-xs leading-relaxed">
          Start with a blank project, or upload imagery and we'll process it for
          you.
        </p>
      </div>
    </button>
  )
}

type StatusFilter = 'all' | 'active' | 'empty'
// A shared All / Folders / Projects filter, surfaced as a dropdown. "Type" is a
// filter here, never a sort key — it decides which kinds appear, not where they
// land; the active sort orders whatever is left as one sequence.
type TypeFilter = 'all' | 'folders' | 'projects'

const TYPE_FILTER_OPTIONS: {
  key: TypeFilter
  label: string
  icon: typeof FolderIcon
}[] = [
  { key: 'all', label: 'All', icon: Squares2X2Icon },
  { key: 'folders', label: 'Folders', icon: FolderIcon },
  { key: 'projects', label: 'Projects', icon: PhotoIcon },
]

// Sort keys map 1:1 to the backend `sortBy`. The field (key) and the direction
// are independent: the dropdown picks the field, a separate toggle flips the
// direction — so the labels are neutral field names, not "Newest"/"Oldest".
type SortKey = ProjectListSortKey & ('name' | 'created' | 'storage' | 'status')

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
  { key: 'name', label: 'Name' },
  { key: 'created', label: 'Created' },
  { key: 'storage', label: 'Storage' },
  { key: 'status', label: 'Status' },
]

const SORT_LABEL: Record<SortKey, string> = {
  name: 'Name',
  created: 'Created',
  storage: 'Storage',
  status: 'Status',
}

// The longest label each dropdown trigger can show. The triggers reserve this
// width (mono font → longest string is widest) so selecting a different value
// never resizes the control and nudges the toolbar.
const longest = (labels: string[]) =>
  labels.reduce((w, l) => (l.length > w.length ? l : w), '')
const WIDEST_TYPE_LABEL = longest(TYPE_FILTER_OPTIONS.map((o) => o.label))
const WIDEST_SORT_LABEL = longest(Object.values(SORT_LABEL))

/**
 * Holds a fixed width for a value that changes: an invisible copy of the widest
 * possible text reserves the space, and the live value renders over it. Keeps a
 * dropdown trigger from resizing (and shifting its neighbours) as the selection
 * changes.
 */
function FixedWidthLabel({
  reserve,
  children,
}: {
  reserve: string
  children: React.ReactNode
}) {
  return (
    <span className="grid text-left">
      <span
        aria-hidden
        className="invisible col-start-1 row-start-1 whitespace-nowrap"
      >
        {reserve}
      </span>
      <span className="col-start-1 row-start-1 whitespace-nowrap">
        {children}
      </span>
    </span>
  )
}

function Projects() {
  const { orgId } = Route.useParams()
  const { metadata } = Route.useRouteContext()
  const navigate = useNavigate()
  const search = Route.useSearch()
  // Current folder id (undefined = org root). URL-driven so folder views are
  // deep-linkable and survive refresh / back-forward.
  const folderId = search.folderId
  const goToFolder = useCallback(
    (next: string | undefined, opts?: { replace?: boolean }) => {
      void navigate({
        to: '/orgs/$orgId/projects',
        params: { orgId },
        search: { folderId: next || undefined },
        replace: opts?.replace ?? false,
      })
    },
    [navigate, orgId],
  )
  const queryClient = useQueryClient()
  const [createProjectOpen, setCreateProjectOpen] = useState(false)
  const [createFolderOpen, setCreateFolderOpen] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  // The folder a new folder is created under (`null` = a new root folder). Set
  // by the toolbar button (current folder) or the tree's "+" affordances.
  const [createFolderParentId, setCreateFolderParentId] = useState<
    string | null
  >(null)
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null)
  const [projectToEdit, setProjectToEdit] = useState<Project | null>(null)
  const [renameErrorId, setRenameErrorId] = useState<string | null>(null)
  const [projectToMove, setProjectToMove] = useState<Project | null>(null)
  const [moveErrorId, setMoveErrorId] = useState<string | null>(null)
  // The drag-to-group pair: source (dragged) dropped onto target.
  const [groupPair, setGroupPair] = useState<{
    source: Project
    target: Project
  } | null>(null)
  const [folderToRename, setFolderToRename] = useState<FolderRef | null>(null)
  const [folderToMove, setFolderToMove] = useState<FolderRef | null>(null)
  const [folderToDelete, setFolderToDelete] = useState<FolderRef | null>(null)
  // The folder tree is hidden by default and toggled from the breadcrumb — the
  // breadcrumb owns "where am I / up"; the tree is the opt-in whole-structure
  // map (cross-branch jumps, drag target). Persisted so it stays as left.
  const [treeOpen, setTreeOpen] = useLocalStorage(
    'projectsFolderTreeOpen',
    false,
  )
  const [filter, setFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all')
  // Sort key + direction persist across the grid/list switch (shared state) and
  // are driven by both the sort menu and the list column headers.
  const [sortKey, setSortKey] = useState<SortKey>('created')
  const [sortDir, setSortDir] = useState<SortDirection>('desc')
  // Field and direction are independent controls: the menu sets the field
  // (keeping the current direction), the toggle flips direction (keeping the
  // field).
  const toggleSortDir = useCallback(
    () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc')),
    [],
  )
  // The active type option drives the dropdown trigger's icon + label.
  const activeTypeOption =
    TYPE_FILTER_OPTIONS.find((o) => o.key === typeFilter) ??
    TYPE_FILTER_OPTIONS[0]
  const ActiveTypeIcon = activeTypeOption.icon
  const { getTableViewMode, setTableViewMode } = useUserPreferences()
  const [viewMode, setViewMode] = useState<ViewMode>(
    () => getTableViewMode(PREFERENCE_KEY) ?? 'grid',
  )
  const handleViewModeChange = useCallback(
    (next: ViewMode) => {
      setViewMode(next)
      setTableViewMode(PREFERENCE_KEY, next)
    },
    [setTableViewMode],
  )

  const { projectClient, userClient, organisationClient, assetClient } =
    useClient()
  const { refreshPermissions } = usePermissions()
  const { hasPermission } = usePermissionCheck()
  const canRenameProject = hasPermission(Permission.UPDATE_PROJECT)
  // Per project as well as org-wide: someone granted a single project (or
  // DroidMap support in an assist session, droidmap#2651) can edit that one.
  // Stable, so the projects table's column memo holds across renders.
  const canManageProject = useCallback(
    (project: Project) =>
      canRenameProject || hasPermission(Permission.UPDATE_PROJECT, project.id),
    [canRenameProject, hasPermission],
  )
  const canDeleteProject = useCallback(
    (project: Project) =>
      hasPermission(Permission.DELETE_PROJECT) ||
      hasPermission(Permission.DELETE_PROJECT, project.id),
    [hasPermission],
  )

  const { userMap } = useOrganisationUsersWithUserMap({
    orgId,
    organisationClient,
    userClient,
    metadata,
  })

  // Pickers for the create dialog's Access section, fetched once the dialog
  // is open. Both walk every page: `useOrganisationUsersWithUserMap` reads
  // only the first page of members, so it can't back a picker that must offer
  // all of them.
  const { data: groups = [] } = useQuery({
    queryKey: ['groups', orgId],
    queryFn: () => fetchGroups(organisationClient, orgId, metadata),
    enabled: createProjectOpen,
  })
  const { data: activeMembers = [] } = useQuery({
    queryKey: ['organisationUsers', orgId, 'all-active'],
    queryFn: () =>
      // Deactivated members would be rejected by the service.
      fetchOrganisationUsers(organisationClient, orgId, metadata, {
        activeOnly: true,
      }),
    enabled: createProjectOpen,
  })
  // Shares the `['user', id]` cache with useOrganisationUsersWithUserMap.
  const memberUserQueries = useQueries({
    queries: activeMembers.map((u) => ({
      queryKey: ['user', u.userId],
      queryFn: () => userClient.getUser({ id: u.userId }, metadata),
    })),
  })
  const byName = (a: { name: string }, b: { name: string }) =>
    a.name.localeCompare(b.name)
  const accessMembers = activeMembers
    .map((u, i) => ({ orgUser: u, user: memberUserQueries[i]?.data }))
    // The creator is already Project Admin.
    .filter(({ orgUser }) => orgUser.userId !== metadata.userId)
    .map(({ orgUser, user }) => ({
      id: orgUser.userId,
      // Fall back to the id until the user record loads.
      name: user
        ? getUserFullName({ [orgUser.userId]: user }, orgUser.userId)
        : orgUser.userId,
    }))
    .sort(byName)
  const accessGroups = useMemo(
    () => groups.map((g) => ({ id: g.id, name: g.name })).sort(byName),
    [groups],
  )

  // Status filter is applied server-side (the contract's `status` param), so
  // pagination/totalCount reflect the filtered set; the toolbar pill counts
  // stay full-dataset (from server statusCounts) so the Active/Empty numbers
  // don't move as you switch between them.
  const trimmedFilter = filter.trim()

  const {
    data: projects,
    rawData,
    isLoading: isLoadingProjects,
    pagination,
  } = useOffsetPaginationQuery<Project, ListProjectsByOrganisationResult>({
    queryKey: projectsQueryKey(orgId, folderId),
    queryFn: ({ page, pageSize }) =>
      fetchProjectsPaginated(orgId, projectClient, metadata, {
        page,
        pageSize,
        filter: trimmedFilter || undefined,
        // Scope to the current folder exactly: root (undefined) → null lists
        // only unfiled projects, so sub-folder projects don't bleed in.
        folderId: folderId ?? null,
        sortBy: sortKey,
        sortDir,
        status: statusFilter === 'all' ? undefined : statusFilter,
      }),
    initialPageSize: 12,
    preferenceKey: PREFERENCE_KEY,
    resetKey: {
      filter: trimmedFilter,
      sort: `${sortKey}:${sortDir}`,
      folder: folderId,
      status: statusFilter,
      // Included even though the type filter isn't a query parameter: it decides
      // whether projects render at all, and the grid only carries folders on
      // page 1. Without the reset, switching to Folders while on page 2 leaves
      // `currentPage` at 2 with nothing to show and no pager to escape with.
      type: typeFilter,
    },
  })

  // Folder tree + current-level folders and every derived lookup (ancestors,
  // name-collision helpers, preview project ids) live in one hook.
  const {
    folderTree,
    hasFolders,
    folderById,
    folders,
    folderNotFound,
    ancestors,
    childNames,
    previewProjectIds,
  } = useOrgFolders({ orgId, folderId, projectClient, metadata })

  // Otherwise the page stays scoped to a folder it can't show, listing nothing
  // under a breadcrumb that can't navigate out. Replace, so Back doesn't return.
  useEffect(() => {
    if (folderNotFound) goToFolder(undefined, { replace: true })
  }, [folderNotFound, goToFolder])

  // New folders (New Folder button, drag-to-group) can only be created while
  // there's room to nest under the current folder (depth < FOLDER_MAX_DEPTH).
  const canGroupHere = ancestors.length < FOLDER_MAX_DEPTH

  // Validate the New Folder input against the name rules and existing siblings
  // under the chosen parent.
  const newFolderNameCollision = childNames(createFolderParentId).some(
    (name) => name.trim().toLowerCase() === newFolderName.trim().toLowerCase(),
  )
  const newFolderNameValid =
    isValidFolderName(newFolderName) && !newFolderNameCollision

  // Pill counts are full-dataset (server statusCounts, aggregated without the
  // status predicate) so the Any/Active/Empty numbers stay stable while a
  // filter is active. `pagination.totalCount` can't back "Any" — it reflects
  // the status-filtered set now that filtering is server-side.
  const statusCounts = rawData?.statusCounts ?? { active: 0, empty: 0 }
  const counts = {
    all: statusCounts.active + statusCounts.empty,
    active: statusCounts.active,
    empty: statusCounts.empty,
  }

  // Project ids whose asset rollups we need: the current project cards plus
  // the folder previews' project children (their cover thumbnails come from the
  // same per-project summary, so both stay consistent). Status filtering is
  // server-side, so `projects` is already the filtered page.
  const projectIds = useMemo(
    () => [...new Set([...projects.map((p) => p.id), ...previewProjectIds])],
    [projects, previewProjectIds],
  )
  const rollups = useProjectRollups(projectIds, assetClient, metadata)

  // Thumbnail URLs for the folder peeks, keyed by project id.
  const previewThumbnails = useMemo(() => {
    const map = new Map<string, string | undefined>()
    for (const id of previewProjectIds) {
      map.set(id, rollups.get(id)?.thumbnailUrl)
    }
    return map
  }, [previewProjectIds, rollups])

  // Mutations can change folder membership and counts, so drop every
  // folder-scoped project page (prefix match) and the folder tree.
  const invalidateProjectsAndFolders = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['projects', orgId] }),
      queryClient.invalidateQueries({ queryKey: ['folders', orgId] }),
      queryClient.invalidateQueries({ queryKey: ['folder-tree', orgId] }),
    ])
  }, [queryClient, orgId])

  const createProjectMutation = useMutation({
    mutationFn: async ({
      name,
      grants,
    }: {
      name: string
      grants: InitialProjectGrant[]
    }) => {
      return await projectClient.createProject(
        {
          name,
          orgId: orgId,
          // New projects land in the folder currently being viewed.
          folderId,
          ...(grants.length > 0 ? { grants } : {}),
        },
        metadata,
      )
    },
    // The dialog stays open until the create succeeds, so a rejected grant
    // (principal no longer in the org) is shown inline with the form intact.
    onSuccess: async () => {
      setCreateProjectOpen(false)
      await refreshPermissions()
      await invalidateProjectsAndFolders()
    },
  })
  const closeCreateProject = () => {
    setCreateProjectOpen(false)
    createProjectMutation.reset()
  }

  const renameProjectMutation = useMutation({
    mutationFn: async (input: { projectId: string; name: string }) =>
      projectClient.updateProject(input, metadata),
    onMutate: () => {
      setRenameErrorId(null)
    },
    onSuccess: async () => {
      setProjectToEdit(null)
      await invalidateProjectsAndFolders()
    },
    onError: (_err, { projectId }) => {
      setRenameErrorId(projectId)
    },
  })

  const moveProjectMutation = useMutation({
    mutationFn: async (input: { projectId: string; folderId: string | null }) =>
      projectClient.updateProject(input, metadata),
    onMutate: () => {
      setMoveErrorId(null)
    },
    onSuccess: async () => {
      setProjectToMove(null)
      await invalidateProjectsAndFolders()
    },
    onError: (_err, { projectId }) => {
      setMoveErrorId(projectId)
    },
  })

  // Drag-to-group: create a new folder under the current folder, then file both
  // the dragged and dropped-on projects into it.
  const groupProjectsMutation = useMutation({
    mutationFn: async (input: { projectIds: string[]; name: string }) => {
      // Create the folder first — if this fails there's nothing to move, and
      // the error surfaces to the modal.
      const folder = await projectClient.createFolder(
        { orgId, name: input.name, parentFolderId: folderId ?? null },
        metadata,
      )
      // allSettled, not all: a partial failure (one project moves, another
      // 403s) must not swallow the successful move. Reject only after every
      // update has settled so onSettled can re-sync the (now partially
      // changed) server state, then surface the failure to the modal.
      const results = await Promise.allSettled(
        input.projectIds.map((projectId) =>
          projectClient.updateProject(
            { projectId, folderId: folder.id },
            metadata,
          ),
        ),
      )
      const failed = results.filter((r) => r.status === 'rejected')
      if (failed.length > 0) {
        throw (failed[0] as PromiseRejectedResult).reason
      }
    },
    onSuccess: () => {
      setGroupPair(null)
    },
    // Invalidate whether or not the batch fully succeeded — a partial failure
    // still moved some projects, so the caches must re-sync either way.
    onSettled: async () => {
      await invalidateProjectsAndFolders()
    },
  })

  // Drag a project onto a tree folder (or the root) → file it there. A drag
  // has no dialog to surface a failure, so on error open the Move dialog for
  // that project (which renders the move error) instead of failing silently.
  const handleProjectDropOnFolder = useCallback(
    (destinationFolderId: string | null, sourceProjectId: string) => {
      const source = projects.find((p) => p.id === sourceProjectId)
      moveProjectMutation.mutate(
        { projectId: sourceProjectId, folderId: destinationFolderId },
        { onError: () => source && setProjectToMove(source) },
      )
    },
    [moveProjectMutation, projects],
  )

  // Create a new folder under the chosen parent (`createFolderParentId`).
  const createFolderMutation = useMutation({
    mutationFn: async (name: string) =>
      projectClient.createFolder(
        { orgId, name, parentFolderId: createFolderParentId },
        metadata,
      ),
    onSuccess: async () => {
      setCreateFolderOpen(false)
      setNewFolderName('')
      await invalidateProjectsAndFolders()
    },
  })

  // Open the New Folder dialog targeting a parent (null = a new root folder).
  const openCreateFolder = useCallback((parentFolderId: string | null) => {
    setCreateFolderParentId(parentFolderId)
    setNewFolderName('')
    createFolderMutation.reset()
    setCreateFolderOpen(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Rename a folder (single call; only the folder's own name changes).
  const renameFolderMutation = useMutation({
    mutationFn: async (input: { folderId: string; name: string }) =>
      projectClient.renameFolder(input, metadata),
    onSuccess: async () => {
      setFolderToRename(null)
      await invalidateProjectsAndFolders()
    },
  })

  // Move a folder (and its subtree) under a new parent (null = org root).
  // Cycle, depth and sibling-name violations are rejected server-side and
  // surface in the modal.
  const moveFolderMutation = useMutation({
    mutationFn: async (input: {
      folderId: string
      parentFolderId: string | null
    }) => projectClient.moveFolder(input, metadata),
    onSuccess: async () => {
      setFolderToMove(null)
      await invalidateProjectsAndFolders()
    },
  })

  // Drag a folder onto a valid folder target (card, row, or tree node; null =
  // the root) → move it there. A drag has no dialog to surface a failure
  // (e.g. the depth cap or a sibling-name conflict), so on error open the
  // Move dialog for that folder, which renders the move error.
  const moveFolderByDrag = useCallback(
    (destParentId: string | null, sourceFolderId: string) => {
      const source = folderById.get(sourceFolderId)
      moveFolderMutation.mutate(
        { folderId: sourceFolderId, parentFolderId: destParentId },
        { onError: () => source && setFolderToMove(source) },
      )
    },
    [folderById, moveFolderMutation],
  )

  // Sensors, drag-in-flight state, folder-move validity and the single drop
  // router (group / file / move) all live in the board dnd hook.
  const {
    sensors: dndSensors,
    activeDrag,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDragCancel: handleDragCancel,
    canDropFolderInto,
  } = useProjectBoardDnd({
    projects,
    folderTree,
    folderById,
    onGroup: (source, target) => setGroupPair({ source, target }),
    onFileProject: handleProjectDropOnFolder,
    onMoveFolder: moveFolderByDrag,
  })

  // Delete a folder. The server rejects (400) a folder that still contains
  // projects or sub-folders; that error is surfaced in the confirm dialog.
  const deleteFolderMutation = useMutation({
    mutationFn: async (folderId: string) =>
      projectClient.deleteFolder({ folderId }, metadata),
    onSuccess: async () => {
      setFolderToDelete(null)
      await invalidateProjectsAndFolders()
    },
  })

  const deleteProjectMutation = useMutation({
    mutationFn: async (projectId: string) => {
      await projectClient.deleteProject({ projectId }, metadata)
    },
    onSuccess: async () => {
      await invalidateProjectsAndFolders()
      setProjectToDelete(null)
    },
  })

  // The list column headers drive the same sort key + direction as the menu.
  const tableSort: ProjectsTableSort = useMemo(
    () => ({ id: sortKey, desc: sortDir === 'desc' }),
    [sortKey, sortDir],
  )

  const handleTableSortChange = useCallback((next: ProjectsTableSort) => {
    setSortKey(next.id)
    setSortDir(next.desc ? 'desc' : 'asc')
  }, [])

  const sortedFolders = useMemo(() => {
    const dir = sortDir === 'desc' ? -1 : 1
    return [...folders].sort((a, b) => {
      if (sortKey === 'created') {
        return (
          dir * (new Date(a.created).getTime() - new Date(b.created).getTime())
        )
      }
      return dir * a.name.localeCompare(b.name)
    })
  }, [folders, sortKey, sortDir])

  const currentPage = pagination.state.pageIndex + 1
  const maxPage = pagination.pageCount ?? 1

  // Type filter — each mode hides one kind and notes how many it is holding
  // back. (totalCount is the server-side project total for the current
  // status/search filter — the projects that would otherwise show.)
  const showFolders = typeFilter !== 'projects'
  const showProjects = typeFilter !== 'folders'
  const hidden =
    typeFilter === 'projects'
      ? { count: sortedFolders.length, noun: 'folder' }
      : typeFilter === 'folders'
        ? { count: pagination.totalCount ?? 0, noun: 'project' }
        : { count: 0, noun: '' }

  /**
   * Grid view renders folders and projects as one sorted sequence, not two
   * bands — the active sort orders across both kinds rather than within each.
   *
   * Two keys can be compared across kinds and two cannot:
   *
   * - `name` and `created` exist on both, so folders and projects interleave.
   * - `storage` and `status` are project-only facts (a folder has neither
   *   aggregate in `FolderSummary`). Inventing a value to sort them by would
   *   put folders in a position that means nothing, so under those keys
   *   folders lead the sequence, ordered among themselves by name — in the
   *   active direction, so `desc` leads with names descending.
   *
   * Folders only join on page 1. Projects are paginated server-side and
   * folders are not, so a folder's true position against pages we haven't
   * fetched is unknowable client-side; repeating them on every page would be
   * worse. With a single page — the common case — the merge is exact.
   */
  const gridItems = useMemo(() => {
    if (viewMode !== 'grid') return []

    type GridItem =
      | { kind: 'folder'; id: string; folder: FolderSummary }
      | { kind: 'project'; id: string; project: Project }

    // `|| !showProjects` is the local guard behind the resetKey fix above: with
    // projects hidden there is no pager and nothing else in the grid, so folders
    // must render whatever `currentPage` happens to be left at. Belt and braces
    // — either alone prevents the blank grid, but the invariant "the grid is
    // never empty while folders exist" should hold without depending on another
    // hook's reset behaviour.
    const folderItems: GridItem[] =
      showFolders && (currentPage === 1 || !showProjects)
        ? sortedFolders.map((folder) => ({
            kind: 'folder',
            id: folder.id,
            folder,
          }))
        : []
    const projectItems: GridItem[] = showProjects
      ? projects.map((project) => ({
          kind: 'project',
          id: project.id,
          project,
        }))
      : []

    // Project-only key: folders lead, both groups keep the order they arrived
    // in (server-sorted projects, name-sorted folders).
    if (sortKey === 'storage' || sortKey === 'status') {
      return [...folderItems, ...projectItems]
    }

    const dir = sortDir === 'desc' ? -1 : 1

    // Decorate once, then sort: the keys are read O(n log n) times, so building
    // a Date per comparison would allocate on every one of them.
    const decorated = [...folderItems, ...projectItems].map((item) => ({
      item,
      name: item.kind === 'folder' ? item.folder.name : item.project.name,
      created: new Date(
        item.kind === 'folder' ? item.folder.created : item.project.created,
      ).getTime(),
    }))

    return decorated
      .sort((a, b) => {
        const by =
          sortKey === 'created'
            ? a.created - b.created
            : a.name.localeCompare(b.name)
        // Stable tiebreak so equal keys (same-second creates) don't reshuffle
        // between renders.
        return dir * by || a.name.localeCompare(b.name)
      })
      .map((d) => d.item)
  }, [
    viewMode,
    showFolders,
    showProjects,
    currentPage,
    sortedFolders,
    projects,
    sortKey,
    sortDir,
  ])

  return (
    // One DndContext spans every drag surface on the page — cards, table
    // rows, and the sidebar tree — so a drag started anywhere can land on any
    // valid target; handleDragEnd routes the drop.
    <DndContext
      sensors={dndSensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragCancel}
    >
      <div>
        {/* Toolbar — search on the left, filters next to it, and the
            sort/view/actions group anchored right. Compact enough to sit on one
            row at desktop widths; wraps (right group first) only when genuinely
            narrow. */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <div className="w-full min-w-0 sm:w-56">
            <Input
              id="project-filter"
              name="filter"
              type="text"
              placeholder="Filter projects…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              leadingIcon={<MagnifyingGlassIcon className="h-3.5 w-3.5" />}
              className="h-10"
            />
          </div>
          {/* Type filter as a compact dropdown (All / Folders / Projects).
              Only meaningful once the org has folders to filter out. */}
          {hasFolders && (
            <FlyoutMenu
              autoCloseDelay={0}
              buttonVariant="outline"
              buttonSize="sm"
              buttonClassName="border-border-strong bg-panel text-text-dim hover:text-text hover:border-accent-tint h-10 gap-2 font-mono text-[11px] uppercase tracking-[0.06em]"
              buttonContent={
                <span className="inline-flex items-center gap-2">
                  <ActiveTypeIcon className="h-3.5 w-3.5 shrink-0" />
                  <FixedWidthLabel reserve={WIDEST_TYPE_LABEL}>
                    {activeTypeOption.label}
                  </FixedWidthLabel>
                </span>
              }
            >
              {TYPE_FILTER_OPTIONS.map(({ key, label, icon }) => (
                <MenuItem
                  key={key}
                  icon={icon}
                  label={typeFilter === key ? `${label} ✓` : label}
                  onClick={() => setTypeFilter(key)}
                />
              ))}
            </FlyoutMenu>
          )}
          {/* Status filter — a projects-only facet, so it drops out entirely
              when the Type filter is showing Folders. */}
          {typeFilter !== 'folders' && (
            <div
              role="group"
              aria-label="Status filter"
              className="border-border-strong bg-bg-2 inline-flex h-10 items-stretch gap-0.5 rounded-md border p-[3px]"
            >
              {(
                [
                  {
                    // "Any" (any status), not "All", so it doesn't read as a
                    // duplicate of the Type dropdown's "All".
                    key: 'all' as StatusFilter,
                    label: 'Any',
                    count: counts.all,
                  },
                  {
                    key: 'active' as StatusFilter,
                    label: 'Active',
                    count: counts.active,
                  },
                  {
                    key: 'empty' as StatusFilter,
                    label: 'Empty',
                    count: counts.empty,
                  },
                ] as const
              ).map(({ key, label, count }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setStatusFilter(key)}
                  className={cn(
                    'flex items-center whitespace-nowrap rounded-sm px-3 font-mono text-[11px] uppercase tracking-[0.06em] transition-colors duration-150',
                    statusFilter === key
                      ? 'bg-panel text-accent shadow-xs'
                      : 'text-text-dim hover:text-text',
                  )}
                >
                  {label} · {count}
                </button>
              ))}
            </div>
          )}
          <div className="flex w-full flex-wrap items-center gap-2.5 sm:ml-auto sm:w-auto sm:flex-nowrap sm:justify-end">
            {/* Sort + view stay grouped so they wrap together on mobile. */}
            <div className="flex items-center gap-2.5">
              {/* Sort control: one pill, two independent zones. The left zone
                  is a dropdown that picks the field (keeping the direction); the
                  right zone toggles direction. List headers drive the same. */}
              <div className="border-border-strong bg-panel inline-flex h-10 items-stretch overflow-hidden rounded-md border font-mono text-[11px] uppercase tracking-[0.06em]">
                <FlyoutMenu
                  autoCloseDelay={0}
                  buttonVariant="ghost"
                  buttonSize="sm"
                  buttonClassName="text-text-dim hover:text-text hover:bg-panel-2 h-full gap-2 rounded-none border-0 font-mono text-[11px] uppercase tracking-[0.06em]"
                  buttonContent={
                    <span className="inline-flex items-center gap-2">
                      <ArrowsUpDownIcon className="h-3 w-3 shrink-0" />
                      <FixedWidthLabel reserve={WIDEST_SORT_LABEL}>
                        {SORT_LABEL[sortKey]}
                      </FixedWidthLabel>
                    </span>
                  }
                >
                  {SORT_OPTIONS.map(({ key, label }) => (
                    <MenuItem
                      key={key}
                      icon={ArrowsUpDownIcon}
                      label={key === sortKey ? `${label} ✓` : label}
                      onClick={() => setSortKey(key)}
                    />
                  ))}
                </FlyoutMenu>
                <span
                  className="bg-border-strong h-5 w-px shrink-0 self-center"
                  aria-hidden
                />
                <button
                  type="button"
                  onClick={toggleSortDir}
                  aria-label={`Sort direction: ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
                  title={sortDir === 'asc' ? 'Ascending' : 'Descending'}
                  className="text-text-dim hover:text-text hover:bg-panel-2 grid w-9 shrink-0 place-items-center transition-colors duration-150"
                >
                  {sortDir === 'asc' ? (
                    <BarsArrowUpIcon className="h-3.5 w-3.5" />
                  ) : (
                    <BarsArrowDownIcon className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
              <ToggleGroup
                type="single"
                value={viewMode}
                onValueChange={(value) => {
                  if (value === 'grid' || value === 'table') {
                    handleViewModeChange(value)
                  }
                }}
                aria-label="View mode"
                className="h-10"
              >
                <ToggleGroupItem
                  value="grid"
                  aria-label="Grid view"
                  title="Grid"
                  className="h-full"
                >
                  <Squares2X2Icon className="h-3.5 w-3.5" />
                </ToggleGroupItem>
                <ToggleGroupItem
                  value="table"
                  aria-label="Table view"
                  title="Table"
                  className="h-full"
                >
                  <Bars3Icon className="h-3.5 w-3.5" />
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            {/* Primary actions — a full-width 50/50 row on mobile so they don't
                strand, natural width at sm+. */}
            <div className="flex w-full items-center gap-2 sm:w-auto sm:gap-2.5">
              {canRenameProject && (
                <Button
                  variant="secondary"
                  // The primary's `shadow-glow` draws its edge as a 1px ring
                  // *outside* the box, so it paints ~1px larger than a normal
                  // inset border. Match it: drop the inset border and give this
                  // button the same outset 1px ring, so the pair paints level.
                  className="flex-1 border-0 shadow-[0_0_0_1px_rgb(var(--border-strong))] sm:flex-none"
                  onClick={() => openCreateFolder(folderId ?? null)}
                  // Disabled at max depth — no room to nest another folder.
                  disabled={!canGroupHere}
                  title={
                    canGroupHere
                      ? undefined
                      : `Folders can't nest deeper than ${FOLDER_MAX_DEPTH} levels`
                  }
                >
                  <FolderPlusIcon className="h-4 w-4" />
                  New Folder
                </Button>
              )}
              <Button
                className="flex-1 sm:flex-none"
                onClick={() => setCreateProjectOpen(true)}
              >
                <PlusIcon className="h-4 w-4" />
                New Project
              </Button>
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-6 md:flex-row">
          {/* Left folder navigation — the opt-in whole-structure map, revealed
            by the breadcrumb's toggle. Hidden by default so the breadcrumb and
            content own the full width; stacks above the content below md. */}
          {hasFolders && treeOpen && (
            <aside className="md:w-72 md:shrink-0">
              {/* Title is visually hidden — the tree's <nav aria-label="Folders">
                names the region for assistive tech. Names truncate, so the
                panel only ever scrolls vertically. */}
              <div className="border-border bg-panel rounded-lg border p-2">
                <div className="max-h-[70vh] overflow-y-auto">
                  <FolderTreeNav
                    folders={folderTree}
                    selectedFolderId={folderId ?? null}
                    onNavigate={(id) => goToFolder(id ?? undefined)}
                    maxDepth={FOLDER_MAX_DEPTH}
                    canManage={canRenameProject}
                    onNewFolder={openCreateFolder}
                    onRename={setFolderToRename}
                    onMove={setFolderToMove}
                    onDelete={setFolderToDelete}
                    dropTargetsEnabled={canRenameProject}
                    canAcceptFolderDrop={canDropFolderInto}
                    onCollapse={() => setTreeOpen(false)}
                  />
                </div>
              </div>
            </aside>
          )}

          <div className="min-w-0 flex-1">
            {/* Location trail — always visible once folders exist: it owns
              "where am I / up", and its leading toggle reveals the tree (the
              whole-structure map) for cross-branch jumps. */}
            {hasFolders && (
              <div className="mb-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTreeOpen((v) => !v)}
                  aria-expanded={treeOpen}
                  aria-label={
                    treeOpen ? 'Hide folder tree' : 'Show folder tree'
                  }
                  title={treeOpen ? 'Hide folder tree' : 'Show folder tree'}
                  className={cn(
                    'grid h-7 w-7 shrink-0 place-items-center rounded-md border transition-colors duration-150',
                    treeOpen
                      ? 'border-accent-tint bg-accent-soft text-accent'
                      : 'border-border bg-panel text-text-dim hover:text-text hover:border-border-strong',
                  )}
                >
                  <ViewColumnsIcon className="h-4 w-4" />
                </button>
                <FolderBreadcrumb
                  className="min-w-0"
                  ancestors={ancestors}
                  onNavigate={(id) => goToFolder(id ?? undefined)}
                />
                {/* Type filter hidden-count note — shares the breadcrumb row,
                    pushed to the right edge. Reads "N folders hidden" under the
                    Projects filter and "N projects hidden" under Folders. */}
                {hidden.count > 0 && (
                  <div className="text-text-mute ml-auto shrink-0 pl-3 font-mono text-[11px] uppercase tracking-[0.06em]">
                    {hidden.count}{' '}
                    {hidden.count === 1 ? hidden.noun : `${hidden.noun}s`}{' '}
                    hidden by filter
                  </div>
                )}
              </div>
            )}
            {/* Body */}
            {isLoadingProjects && projects.length === 0 ? (
              <div className="flex justify-center py-16">
                <Spinner size="page" />
              </div>
            ) : viewMode === 'table' ? (
              <ProjectsTable
                // A 'folders' type filter hides project rows (empty data) and
                // neutralises the pagination footer, since it counts projects.
                projects={showProjects ? projects : []}
                userMap={userMap}
                rollups={rollups}
                orgId={orgId}
                isLoading={isLoadingProjects}
                paginationConfig={
                  showProjects
                    ? pagination
                    : {
                        ...pagination,
                        totalCount: 0,
                        pageCount: 1,
                        hasMoreBeyondKnownPages: false,
                      }
                }
                sort={tableSort}
                onSortChange={handleTableSortChange}
                onEdit={setProjectToEdit}
                onMove={setProjectToMove}
                onDelete={setProjectToDelete}
                canManageProject={canManageProject}
                canDeleteProject={canDeleteProject}
                // Rows are drag sources + drop targets whenever the caller can
                // move projects. Filing into a folder and grouping two projects
                // both work at any depth; if grouping would nest too deep, the
                // folder-creation call surfaces the depth error.
                draggableRows={canRenameProject}
                dropTargetsEnabled={canRenameProject}
                folders={showFolders ? sortedFolders : undefined}
                onFolderOpen={(f) => goToFolder(f.id)}
                onFolderRename={
                  canRenameProject ? setFolderToRename : undefined
                }
                onFolderMove={canRenameProject ? setFolderToMove : undefined}
                onFolderDelete={
                  canRenameProject ? setFolderToDelete : undefined
                }
                canAcceptFolderDrop={(f, sourceId) =>
                  canDropFolderInto(f.id, sourceId)
                }
                previewThumbnails={previewThumbnails}
              />
            ) : (
              // One sequence, both kinds, ordered by the active sort — no
              // folder band, no section split. The card shapes carry the
              // difference (a folder card is a 2×2 mosaic of its contents, a
              // project card a single hero and a stat strip), so the two read
              // apart without needing to be kept apart.
              <section aria-label="Folders and projects">
                {/* `auto-rows-fr` sizes every row to the tallest card, so a row
                    of only empty projects (no stat strip) doesn't come up short
                    against a row that has an active project. */}
                <div className={cn(SECTION_GRID, 'auto-rows-fr')}>
                  {gridItems.map((item) => (
                    <div key={`${item.kind}:${item.id}`} className="col-span-2">
                      {item.kind === 'folder' ? (
                        <FolderCard
                          folder={item.folder}
                          onOpen={(f) => goToFolder(f.id)}
                          onRename={
                            canRenameProject ? setFolderToRename : undefined
                          }
                          onMove={
                            canRenameProject ? setFolderToMove : undefined
                          }
                          onDelete={
                            canRenameProject ? setFolderToDelete : undefined
                          }
                          draggable={canRenameProject}
                          droppable={canRenameProject}
                          canAcceptFolderDrop={(f, sourceId) =>
                            canDropFolderInto(f.id, sourceId)
                          }
                          previewThumbnails={previewThumbnails}
                        />
                      ) : (
                        <ProjectCard
                          project={item.project}
                          rollup={rollups.get(item.project.id)}
                          creator={userFor(userMap, item.project.createdBy)}
                          orgId={orgId}
                          onDelete={
                            canDeleteProject(item.project)
                              ? setProjectToDelete
                              : undefined
                          }
                          onEdit={
                            canManageProject(item.project)
                              ? setProjectToEdit
                              : undefined
                          }
                          onMove={
                            canManageProject(item.project)
                              ? setProjectToMove
                              : undefined
                          }
                          draggable={canRenameProject}
                          droppable={canRenameProject}
                        />
                      )}
                    </div>
                  ))}
                  {showProjects && currentPage === 1 && (
                    <div className="col-span-2">
                      <CreateProjectCard
                        onClick={() => setCreateProjectOpen(true)}
                      />
                    </div>
                  )}
                </div>
              </section>
            )}

            {viewMode === 'grid' && showProjects && maxPage > 1 && (
              <div className="mt-6 flex justify-center">
                <Pagination
                  currentPage={currentPage}
                  maxPage={maxPage}
                  hasMoreBeyondKnownPages={
                    pagination.hasMoreBeyondKnownPages ?? false
                  }
                  onPageChange={(page) =>
                    pagination.onChange({
                      pageIndex: page - 1,
                      pageSize: pagination.state.pageSize,
                    })
                  }
                />
              </div>
            )}
          </div>
        </div>

        <CreateProjectModal
          isOpen={createProjectOpen}
          onClose={closeCreateProject}
          onSubmit={(input) => createProjectMutation.mutate(input)}
          isPending={createProjectMutation.isPending}
          error={
            createProjectMutation.isError
              ? (serverRefusalMessage(createProjectMutation.error) ??
                'The project could not be created. Please try again.')
              : undefined
          }
          onEdit={() => {
            if (createProjectMutation.isError) createProjectMutation.reset()
          }}
          members={accessMembers}
          groups={accessGroups}
          creatorName={
            userMap[metadata.userId]
              ? getUserFullName(userMap, metadata.userId)
              : undefined
          }
        />

        <Modal
          title="New Folder"
          icon={<FolderPlusIcon />}
          isOpen={createFolderOpen}
          onClose={() => {
            setCreateFolderOpen(false)
            setNewFolderName('')
            createFolderMutation.reset()
          }}
          maxWidth="sm:max-w-lg"
        >
          <form
            onSubmit={(event) => {
              event.preventDefault()
              const name = newFolderName.trim()
              if (!newFolderNameValid) return
              createFolderMutation.mutate(name)
            }}
            className="space-y-6"
          >
            <Input
              label="Folder Name"
              id="folderName"
              name="folderName"
              type="text"
              autoFocus={true}
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
            />
            {newFolderName.trim().length > 0 && !newFolderNameValid && (
              <p className="text-danger text-sm">
                {newFolderNameCollision
                  ? 'A folder with this name already exists here'
                  : "A folder name is required and can't contain '/'"}
              </p>
            )}
            <ModalFormError
              error={
                createFolderMutation.isError ? createFolderMutation.error : null
              }
              fallback="An error occurred while creating the folder."
            />
            <div className="mt-5 flex flex-row-reverse gap-2 sm:mt-4">
              <Button
                type="submit"
                variant="creative"
                disabled={!newFolderNameValid}
                isLoading={createFolderMutation.isPending}
              >
                Create
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setCreateFolderOpen(false)
                  setNewFolderName('')
                  createFolderMutation.reset()
                }}
                disabled={createFolderMutation.isPending}
              >
                Cancel
              </Button>
            </div>
          </form>
        </Modal>

        <EditNameModal
          isOpen={projectToEdit !== null}
          onClose={() => {
            setProjectToEdit(null)
            setRenameErrorId(null)
          }}
          entityType="Project"
          initialName={projectToEdit?.name ?? ''}
          isPending={renameProjectMutation.isPending}
          error={
            renameProjectMutation.isError && renameErrorId === projectToEdit?.id
              ? renameProjectMutation.error
              : null
          }
          onSubmit={(newName) => {
            if (!projectToEdit) return
            renameProjectMutation.mutate({
              projectId: projectToEdit.id,
              name: newName,
            })
          }}
        />

        <MoveToFolderModal
          isOpen={projectToMove !== null}
          onClose={() => {
            setProjectToMove(null)
            setMoveErrorId(null)
          }}
          subject={
            projectToMove && {
              kind: 'project',
              name: projectToMove.name,
              currentFolderId: projectToMove.folderId ?? null,
            }
          }
          folders={folderTree}
          isPending={moveProjectMutation.isPending}
          error={
            moveProjectMutation.isError && moveErrorId === projectToMove?.id
              ? moveProjectMutation.error
              : null
          }
          onSubmit={(destination) => {
            if (!projectToMove) return
            moveProjectMutation.mutate({
              projectId: projectToMove.id,
              folderId: destination,
            })
          }}
        />

        <GroupProjectsModal
          isOpen={groupPair !== null}
          onClose={() => setGroupPair(null)}
          sourceProjectName={groupPair?.source.name ?? ''}
          targetProjectName={groupPair?.target.name ?? ''}
          existingFolderNames={childNames(folderId ?? null)}
          isPending={groupProjectsMutation.isPending}
          error={
            groupProjectsMutation.isError ? groupProjectsMutation.error : null
          }
          onSubmit={(folderName) => {
            if (!groupPair) return
            groupProjectsMutation.mutate({
              projectIds: [groupPair.source.id, groupPair.target.id],
              name: folderName,
            })
          }}
        />

        <MoveToFolderModal
          isOpen={folderToMove !== null}
          onClose={() => {
            setFolderToMove(null)
            moveFolderMutation.reset()
          }}
          subject={folderToMove && { kind: 'folder', folder: folderToMove }}
          folders={folderTree}
          isPending={moveFolderMutation.isPending}
          error={moveFolderMutation.isError ? moveFolderMutation.error : null}
          onSubmit={(parentFolderId) => {
            if (!folderToMove) return
            moveFolderMutation.mutate({
              folderId: folderToMove.id,
              parentFolderId,
            })
          }}
        />

        <RenameFolderModal
          isOpen={folderToRename !== null}
          onClose={() => setFolderToRename(null)}
          folderName={folderToRename?.name ?? ''}
          siblingNames={childNames(folderToRename?.parentFolderId ?? null)}
          isPending={renameFolderMutation.isPending}
          error={
            renameFolderMutation.isError ? renameFolderMutation.error : null
          }
          onSubmit={(newName) => {
            if (!folderToRename) return
            renameFolderMutation.mutate({
              folderId: folderToRename.id,
              name: newName,
            })
          }}
        />

        {folderToDelete && (
          <ConfirmationDialog
            title="Delete folder"
            description={
              <>
                You&rsquo;re about to delete the folder{' '}
                <strong className="font-semibold text-text">
                  {folderToDelete.name}
                </strong>
                . It must be empty first.
              </>
            }
            error={
              <ModalFormError
                error={
                  deleteFolderMutation.isError
                    ? deleteFolderMutation.error
                    : null
                }
                fallback="An error occurred while deleting the folder."
              />
            }
            isOpen={folderToDelete !== null}
            onClose={() => {
              setFolderToDelete(null)
              deleteFolderMutation.reset()
            }}
            onConfirm={() => {
              if (folderToDelete) {
                deleteFolderMutation.mutate(folderToDelete.id)
              }
            }}
            confirmText="Delete"
            cancelText="Cancel"
            variant="destructive"
            isLoading={deleteFolderMutation.isPending}
          />
        )}

        {projectToDelete && (
          <ConfirmationDialog
            title="Delete Project"
            description={
              <>
                You&rsquo;re about to permanently delete{' '}
                <strong className="font-semibold text-text">
                  {projectToDelete.name}
                </strong>
                , including all of its asset sets, tasks, and outputs. This
                can&rsquo;t be undone.
              </>
            }
            isOpen={projectToDelete !== null}
            onClose={() => setProjectToDelete(null)}
            onConfirm={() => {
              if (projectToDelete) {
                deleteProjectMutation.mutate(projectToDelete.id)
              }
            }}
            confirmOptions={{
              code: projectToDelete.name,
            }}
            confirmText="Delete"
            cancelText="Cancel"
            variant="destructive"
            isLoading={deleteProjectMutation.isPending}
          />
        )}
      </div>

      {/* Compact chip following the pointer while something is dragged —
          replaces the browser's ghost image with a consistent preview.
          snapCenterToCursor keeps the chip under the pointer: without it the
          overlay is anchored to the grabbed card's rect, so grabbing a wide
          card away from its corner leaves the chip floating far from the
          cursor. */}
      <DragOverlay dropAnimation={null} modifiers={[snapCenterToCursor]}>
        {activeDrag && (
          <div className="border-border bg-panel shadow-elevated text-text flex w-max items-center gap-2 rounded-md border px-3 py-1.5 text-sm">
            {activeDrag.kind === 'folder' ? (
              <FolderIcon aria-hidden className="text-text-dim h-4 w-4" />
            ) : (
              <PhotoIcon aria-hidden className="text-text-dim h-4 w-4" />
            )}
            <span className="max-w-48 truncate">{activeDrag.name}</span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  )
}
